import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';

import { useAuthenticator } from '@aws-amplify/ui-react';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import CloudUploadIcon from '@mui/icons-material/CloudUpload';
import { Box, Button, Chip, CircularProgress, Divider, Typography } from '@mui/material';

import PageHeader from '../../components/PageHeader';
import { useDealAssessmentFileUrlLazyQuery } from '../../gql/generated/graphql';
import CustomPaper from '../DFMPage/components/CustomPaper/CustomPaper';

import UploadExcelFile from './components/UploadExcelFile';
import ValidateBenchmarkingExcel from './components/ValidateBenchmarkingExcel';
import { VisuallyHiddenInput } from './components/VisuallyHiddenInput';

import styles from './styles';

const InputFileUpload = ({ onProgress, onFileLoaded }) => {
  const { t } = useTranslation();
  const [isLoading, setIsLoading] = useState(false);

  const handleFileChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    onProgress(0); // reset progress
    setIsLoading(true);

    const file = event.target.files?.[0];
    if (!file) return;

    const reader = new FileReader();

    reader.readAsArrayBuffer(file);
    reader.onprogress = event => {
      if (event?.lengthComputable) {
        const progress = (event.loaded / event.total) * 100;
        onProgress(progress);
      }
    };

    reader.onload = event => {
      const fileContents = event.target?.result;
      onFileLoaded(fileContents); // call the callback with the file contents
      setIsLoading(false);
    };

    reader.onerror = error => {
      console.error('Error reading file:', error);
      setIsLoading(false);
    };
  };

  return (
    <Box display="flex" alignItems="center" gap={2} width="100%">
      <Button component="label" variant="contained" startIcon={<CloudUploadIcon />} sx={styles.uploadButton}>
        {isLoading ? (
          <>
            {t('benchmarking.loadingIndicator')} <CircularProgress size={20} color="info" />
          </>
        ) : (
          <>
            {t('benchmarking.fileUpload')}
            <VisuallyHiddenInput type="file" accept=".xlsx" onChange={handleFileChange} />
          </>
        )}
      </Button>
      {/* <LinearProgress variant="determinate" value={0} sx={styles.linearProgress} /> */}
    </Box>
  );
};

const BenchmarkingOverview = () => {
  const { t } = useTranslation();
  const { user } = useAuthenticator();
  const [progress, setProgress] = useState(0);
  const [fileContents, setFileContents] = useState<ArrayBuffer | null>(null);
  const [validationSuccess, setValidationSuccess] = useState(false);
  const [companyID, setCompanyID] = useState<number | null>(null);
  const [completedSheets, setCompletedSheets] = useState<string[]>([]);
  const [uploadComplete, setUploadComplete] = useState(false);

  const [getDealAssessmentFileUrl, { data: dealAssessmentFileUrl }] = useDealAssessmentFileUrlLazyQuery({});

  const dealAssessmentFileTriggered =
    dealAssessmentFileUrl?.getCompany?.edges?.[0]?.node?.dealAssessmentFileUrl?.previousGenerationStillRunning;

  const handleValidationComplete = (success: boolean, companyID: number | null) => {
    setValidationSuccess(success);
    setCompanyID(companyID);
    // Reset upload states when new file is validated
    setCompletedSheets([]);
    setUploadComplete(false);
  };

  const handleSheetCompletion = (sheetName: string) => {
    setCompletedSheets(prev => [...prev, sheetName]);
  };

  const handleFileCompletion = () => {
    setUploadComplete(true);
    getDealAssessmentFileUrl({ variables: { companyId: companyID } });
  };

  return (
    <Box>
      <PageHeader title="Benchmarking" subtitle="Upload and validate benchmarking data" variant="default" />
      <Box p={3}>
        <CustomPaper sx={styles.paper}>
          <Box display="flex" flexDirection="column" gap={2}>
            <InputFileUpload onProgress={setProgress} onFileLoaded={setFileContents} />

            {fileContents && (
              <>
                <ValidateBenchmarkingExcel
                  fileContents={fileContents}
                  onValidationComplete={handleValidationComplete}
                  onGenerationTriggered={getDealAssessmentFileUrl}
                />
                <Divider sx={styles.divider} />
              </>
            )}

            <Box display="flex" flexDirection="column" gap={2}>
              {validationSuccess && (
                <>
                  <UploadExcelFile
                    fileContents={fileContents}
                    companyId={companyID}
                    fileName="uploaded_file.xlsx"
                    disableUpload={dealAssessmentFileTriggered || !validationSuccess || uploadComplete}
                    uploaderName={user?.username}
                    onProgress={progress => console.log(`Progress: ${progress}%`)}
                    onSheetCompletion={handleSheetCompletion}
                    onFileCompletion={handleFileCompletion}
                    validationSuccess={validationSuccess}
                  />

                  {completedSheets.length > 0 && (
                    <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1, mt: 1 }}>
                      {completedSheets.map(sheetName => (
                        <Chip
                          key={sheetName}
                          label={`${sheetName} uploaded`}
                          color="success"
                          size="small"
                          icon={<CheckCircleIcon />}
                        />
                      ))}
                    </Box>
                  )}

                  {uploadComplete && (
                    <Box sx={{ mt: 2, p: 2, bgcolor: 'success.light', borderRadius: 1 }}>
                      <Typography>
                        {t(
                          'benchmarking.uploadComplete',
                          'Data was uploaded and DA generation has been triggered successfully. You will receive an email when the file is ready.'
                        )}
                      </Typography>
                    </Box>
                  )}
                </>
              )}
            </Box>
          </Box>
        </CustomPaper>
      </Box>
    </Box>
  );
};

export default BenchmarkingOverview;
